/**
 * <MediaPicker> — the media attachment picker (organism, L0.1).
 *
 * The maison rebuild of the legacy `MediaPickerButton`: an add button that opens an `<ActionSheet>` of
 * sources (link / camera / gallery / document), the resulting attachment previews (`<ImageAttachment>` /
 * `<PDFAttachment>` / `<LinkAttachment>`),
 * a URL `<Modal>`, a delete `<ConfirmDialog>` and a full-screen `<MediaDisplay>` for image / pdf / link
 * (owned internally; tapping a preview opens the matching viewer). It owns ALL the orchestration —
 * append-vs-replace, pdf-vs-image routing, delete confirmation, URL validate flow, menu + loading,
 * viewing — but imports nothing forbidden: every side effect (gallery/document/camera pickers, URL
 * validation) is **injected as an adapter callback** by the caller (see
 * `ports/primary/ui/hooks/useMediaPickerAdapter`), so the organism stays isolated and Storybook-renderable.
 *
 * Web extras (parity with activity `ImageUploadZone`): when `enableDragAndDrop` is on (default), the
 * empty / "add more" dashed surface accepts drag-and-drop + Ctrl+V paste (images only for paste).
 * Gallery is mobile-only — hidden on web even if `enableGallery` is true.
 *
 * Controlled: `images` / `pdf` / `url` come in, `onImagesChange` / `onPdfChange` / `onUrlChange` go out.
 * The camera returns photos asynchronously (via redux, app-side), so they arrive through
 * `incomingCameraImages` and are acknowledged with `onCameraImagesConsumed`.
 *
 * `enableVideo` (défaut `false`, le sélecteur d'avant) : les vidéos se joignent aussi — la galerie et
 * le choix de fichier les proposent (l'adaptateur), le glisser-déposer les accepte, et chaque pièce
 * garde son `mimeType`, son nom et son poids pour que l'écran juge ce qu'il envoie. Une vidéo se montre
 * en vignette de vidéo et n'ouvre pas la visionneuse d'images.
 */
import React, { useEffect, useReducer } from "react"
import { Platform, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { DashedButton } from "../../atoms/DashedButton"
import { TextInput } from "../../atoms/TextInput"
import { Button } from "../../atoms/Buttons/Button"
import { type IconName } from "../../atoms/icons/Icon"
import { ActionSheet } from "../../molecules/ActionSheet"
import { ImageAttachment } from "../../molecules/ImageAttachment"
import { PDFAttachment } from "../../molecules/PDFAttachment"
import { LinkAttachment } from "../../molecules/LinkAttachment"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { Modal } from "../../molecules/Modal"
import { MediaDisplay } from "../MediaDisplay"
import { DropTarget } from "./DropTarget"
import { droppedMediaAction, isAcceptedDrop, isPdfUri, type DroppedMedia } from "./mediaDrop"
import { soleMediaSource } from "./mediaMenu"

type MediaImage = {
    uri: string
    width?: number
    height?: number
    /** Avec `enableVideo` : de quoi juger la pièce (son type, son nom, son poids en octets). */
    mimeType?: string
    name?: string
    fileSize?: number
}

/** What the pending delete confirmation targets. */
type ConfirmTarget = { type: "image"; index: number } | { type: "pdf" } | { type: "url" }

type ViewerState = { kind: "image" } | { kind: "pdf"; uri: string } | { kind: "link"; url: string }

type UiState = {
    picking: boolean
    dragging: boolean
    menuVisible: boolean
    url: { open: boolean; text: string; error: string | null }
    confirm: ConfirmTarget | null
    viewer: ViewerState | null
}

type UiAction =
    | { type: "PICKING"; value: boolean }
    | { type: "DRAGGING"; value: boolean }
    | { type: "MENU"; open: boolean }
    | { type: "URL_OPEN"; text: string }
    | { type: "URL_TEXT"; text: string }
    | { type: "URL_ERROR"; error: string | null }
    | { type: "URL_CLOSE" }
    | { type: "CONFIRM"; target: ConfirmTarget | null }
    | { type: "VIEWER"; viewer: ViewerState | null }

const initialUi: UiState = {
    picking: false,
    dragging: false,
    menuVisible: false,
    url: { open: false, text: "", error: null },
    confirm: null,
    viewer: null,
}

function uiReducer(state: UiState, action: UiAction): UiState {
    switch (action.type) {
        case "PICKING":
            return { ...state, picking: action.value }
        case "DRAGGING":
            return state.dragging === action.value ? state : { ...state, dragging: action.value }
        case "MENU":
            return { ...state, menuVisible: action.open }
        case "URL_OPEN":
            return { ...state, url: { open: true, text: action.text, error: null } }
        case "URL_TEXT":
            return { ...state, url: { ...state.url, text: action.text, error: null } }
        case "URL_ERROR":
            return { ...state, url: { ...state.url, error: action.error } }
        case "URL_CLOSE":
            return { ...state, url: { ...state.url, open: false, error: null } }
        case "CONFIRM":
            return { ...state, confirm: action.target }
        case "VIEWER":
            return { ...state, viewer: action.viewer }
        default:
            return state
    }
}

export type MediaPickerProps = {
    // Controlled value (owned by the caller/form).
    images?: MediaImage[]
    pdf?: string | null
    url?: string
    onImagesChange?: (next: MediaImage[]) => void
    onPdfChange?: (next: string | null) => void
    onUrlChange?: (next: string) => void

    // Injected effect adapters (the only forbidden-dep touch points; supplied by the container).
    /** Open the gallery and resolve the picked image(s). */
    pickImages?: () => Promise<MediaImage[]>
    /** Open the document picker and resolve the picked file (pdf or image). */
    pickDocument?: () => Promise<DroppedMedia | null>
    /** Fire the camera flow (photos return via `incomingCameraImages`). */
    launchCamera?: () => void
    /** Pure URL validation — invalid input keeps the dialog open with an error. */
    validateUrl?: (url: string) => boolean

    // Camera round-trip bridge (redux → props, done in the container).
    incomingCameraImages?: MediaImage[]
    onCameraImagesConsumed?: () => void

    // Config (mirrors the legacy button).
    multiple?: boolean
    buttonLabel?: string
    addMoreButtonLabel?: string
    showAddMoreButton?: boolean
    enableExternalLink?: boolean
    enableCamera?: boolean
    enableGallery?: boolean
    enableDocument?: boolean
    /** Web drag-and-drop + Ctrl+V paste on the dashed surface. No-op on native. Default true. */
    enableDragAndDrop?: boolean
    videoCourseMode?: boolean
    /** Les vidéos se joignent aussi (galerie, fichier, glisser-déposer). Défaut `false`. */
    enableVideo?: boolean
    minHeight?: number

    // Parent-modal coordination.
    onMenuStateChange?: (open: boolean) => void
    testID?: string
}

const IS_WEB = Platform.OS === "web"

const isVideoMedia = (media: { mimeType?: string }): boolean => media.mimeType?.startsWith("video/") === true

/** Web drag event shape (DOM) — handlers are attached via `<DropTarget>` (a real `<div>` on web). */
type WebDragEvent = {
    preventDefault: () => void
    stopPropagation: () => void
    dataTransfer?: { files?: FileList | null }
}

export function MediaPicker(props: MediaPickerProps) {
    const {
        images = [],
        pdf = null,
        url = "",
        onImagesChange,
        onPdfChange,
        onUrlChange,
        pickImages,
        pickDocument,
        launchCamera,
        validateUrl,
        incomingCameraImages,
        onCameraImagesConsumed,
        multiple = false,
        buttonLabel = "Ajouter",
        addMoreButtonLabel = "Ajouter plus",
        showAddMoreButton = true,
        enableExternalLink = true,
        enableCamera = true,
        enableGallery = true,
        enableDocument = true,
        enableDragAndDrop = true,
        videoCourseMode = false,
        enableVideo = false,
        minHeight = 180,
        onMenuStateChange,
        testID,
    } = props

    const [ui, dispatch] = useReducer(uiReducer, initialUi)

    // Gallery is mobile-only (legacy intent) — web uses document picker + drag/drop instead.
    const galleryEnabled = enableGallery && !IS_WEB
    const dropEnabled = enableDragAndDrop && IS_WEB

    const hasPdf = (pdf != null && pdf !== "") || (images.length > 0 && isPdfUri(images[0]?.uri))
    const hasImages = images.length > 0 && !hasPdf
    const hasUrl = !!url && !hasPdf && !hasImages
    const viewableImages = enableVideo ? images.filter((image) => !isVideoMedia(image)) : images

    const mergeImages = (assets: MediaImage[]) => {
        if (!assets.length) return
        onImagesChange?.(multiple ? [...images, ...assets] : [assets[0]])
    }

    const ingestDropped = (file: DroppedMedia) => {
        const action = droppedMediaAction(file, { takesPdf: onPdfChange != null && !enableVideo })
        if (action === "ignore") return
        if (action === "pdf") {
            onPdfChange?.(file.uri)
            onImagesChange?.([])
            return
        }
        // Avec les vidéos, la pièce garde de quoi être jugée ; sans, l'image d'avant (l'adresse seule).
        mergeImages([
            enableVideo
                ? { uri: file.uri, mimeType: file.mimeType, name: file.name, fileSize: file.fileSize }
                : { uri: file.uri },
        ])
    }

    /** Legacy ImageUploadZone: first accepted file only. */
    const ingestFileList = (fileList: FileList | null | undefined) => {
        if (!fileList?.length) return
        const files = Array.from(fileList).filter((dropped) => isAcceptedDrop(dropped, enableVideo))
        const file = files[0]
        if (!file) return
        ingestDropped({
            uri: URL.createObjectURL(file),
            name: file.name,
            mimeType: file.type,
            fileSize: file.size,
        })
    }

    const handleGallery = async () => {
        if (!pickImages) return
        dispatch({ type: "PICKING", value: true })
        try {
            const assets = await pickImages()
            if (assets?.length) mergeImages(assets)
        } finally {
            dispatch({ type: "PICKING", value: false })
        }
    }

    const handleDocument = async () => {
        if (!pickDocument) return
        dispatch({ type: "PICKING", value: true })
        try {
            const doc = await pickDocument()
            if (doc) ingestDropped(doc)
        } finally {
            dispatch({ type: "PICKING", value: false })
        }
    }

    // Camera photos arrive asynchronously (redux, app-side) — ingest and acknowledge so the container
    // can clear them. Dedupe by uri (mirrors the legacy sync).
    useEffect(() => {
        if (!incomingCameraImages || incomingCameraImages.length === 0) return
        const existing = new Set(images.map((i) => i.uri))
        const fresh = incomingCameraImages.filter((i) => !existing.has(i.uri))
        if (fresh.length) mergeImages(fresh)
        onCameraImagesConsumed?.()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when new camera photos arrive
    }, [incomingCameraImages])

    // Ctrl+V paste (web) — images only, parity with activity ImageUploadZone.
    useEffect(() => {
        if (!dropEnabled) return

        const handlePaste = (event: ClipboardEvent) => {
            const items = event.clipboardData?.items
            if (!items) return
            for (const item of Array.from(items)) {
                if (!item.type.startsWith("image/")) continue
                event.preventDefault()
                const file = item.getAsFile()
                if (!file) break
                ingestDropped({
                    uri: URL.createObjectURL(file),
                    name: file.name || `pasted-image.${file.type.split("/")[1] ?? "png"}`,
                    mimeType: file.type,
                })
                break
            }
        }

        document.addEventListener("paste", handlePaste)
        return () => document.removeEventListener("paste", handlePaste)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- paste uses latest ingest via closure; rebind when dropEnabled / media setters change
    }, [dropEnabled, images, multiple, onImagesChange, onPdfChange])

    const openMenu = () => {
        dispatch({ type: "MENU", open: true })
        onMenuStateChange?.(true)
    }
    const closeMenu = () => {
        dispatch({ type: "MENU", open: false })
        onMenuStateChange?.(false)
    }

    const openUrlDialog = () => {
        dispatch({ type: "URL_OPEN", text: url ?? "" })
    }
    const submitUrl = () => {
        const ok = validateUrl ? validateUrl(ui.url.text) : true
        if (ok) {
            onUrlChange?.(ui.url.text)
            dispatch({ type: "URL_CLOSE" })
        } else {
            dispatch({ type: "URL_ERROR", error: "URL invalide" })
        }
    }

    const applyRemove = (target: ConfirmTarget) => {
        if (target.type === "image") {
            onImagesChange?.(images.filter((_, i) => i !== target.index))
        } else if (target.type === "pdf") {
            // Clear both fields — a pdf may live in `pdf` (picked) or as an inherited `images[0]`.
            onPdfChange?.(null)
            onImagesChange?.([])
        } else {
            onUrlChange?.("")
        }
    }
    const confirmTitle = (target: ConfirmTarget | null): string => {
        if (target?.type === "pdf") return "Retirer le fichier ?"
        if (target?.type === "url") return "Retirer le lien ?"
        return "Retirer l'image ?"
    }

    const menuItems: { icon: IconName; label: string; onPress: () => void }[] = []
    if (enableExternalLink) menuItems.push({ icon: "link", label: "Lien externe", onPress: openUrlDialog })
    if (enableCamera) menuItems.push({ icon: "camera", label: "Prendre une photo", onPress: () => launchCamera?.() })
    if (galleryEnabled) menuItems.push({ icon: "file-image", label: "Ouvrir la galerie", onPress: handleGallery })
    if (enableDocument)
        menuItems.push({
            icon: "file",
            label: enableVideo ? "Choisir un fichier (image, vidéo)" : "Choisir un document (pdf, image)",
            onPress: handleDocument,
        })
    // Au web, une feuille à une seule source ne s'ouvre pas : le bouton lance la source, dans le même
    // toucher (un sélecteur de fichiers hors d'un geste est bloqué par le navigateur).
    const onAdd = soleMediaSource(menuItems, IS_WEB)?.onPress ?? openMenu

    // Web drag handlers on the dashed surface only (empty / add-more) — not over previews.
    // Must use <DropTarget> (DOM div on web): RN-web <View> strips onDrop / onDragOver.
    const dropHandlers = dropEnabled
        ? {
              onDragEnter: (event: WebDragEvent) => {
                  event.preventDefault()
                  event.stopPropagation()
                  dispatch({ type: "DRAGGING", value: true })
              },
              onDragOver: (event: WebDragEvent) => {
                  event.preventDefault()
                  event.stopPropagation()
                  dispatch({ type: "DRAGGING", value: true })
              },
              onDragLeave: (event: WebDragEvent) => {
                  event.preventDefault()
                  event.stopPropagation()
                  dispatch({ type: "DRAGGING", value: false })
              },
              onDrop: (event: WebDragEvent) => {
                  event.preventDefault()
                  event.stopPropagation()
                  dispatch({ type: "DRAGGING", value: false })
                  ingestFileList(event.dataTransfer?.files)
              },
          }
        : undefined

    const renderDashed = (label: string, height: number, onPress: () => void) => {
        const button = (
            <DashedButton
                label={ui.dragging ? "Déposer le fichier ici" : label}
                minHeight={height}
                loading={ui.picking}
                highlighted={ui.dragging}
                onPress={onPress}
            />
        )
        if (!dropHandlers) return button
        return <DropTarget {...dropHandlers}>{button}</DropTarget>
    }

    let content: React.ReactNode
    if (videoCourseMode) {
        content = (
            <DashedButton label={buttonLabel} minHeight={minHeight} loading={ui.picking} onPress={openUrlDialog} />
        )
    } else if (hasUrl) {
        content = (
            <LinkAttachment
                url={url}
                onOpen={() => dispatch({ type: "VIEWER", viewer: { kind: "link", url } })}
                onDelete={() => dispatch({ type: "CONFIRM", target: { type: "url" } })}
            />
        )
    } else if (hasPdf) {
        const pdfUri = pdf || images[0]?.uri || ""
        content = (
            <PDFAttachment
                onOpen={() => dispatch({ type: "VIEWER", viewer: { kind: "pdf", uri: pdfUri } })}
                onDelete={() => dispatch({ type: "CONFIRM", target: { type: "pdf" } })}
            />
        )
    } else if (hasImages) {
        content = (
            <>
                <ImageAttachment
                    images={images}
                    // La visionneuse ne montre que des images : des vidéos seules ne l'ouvrent pas.
                    onOpen={
                        viewableImages.length > 0
                            ? () => dispatch({ type: "VIEWER", viewer: { kind: "image" } })
                            : undefined
                    }
                    onDeleteImage={(index) => dispatch({ type: "CONFIRM", target: { type: "image", index } })}
                />
                {multiple && showAddMoreButton && (
                    <View style={styles.addMore}>{renderDashed(addMoreButtonLabel, 72, onAdd)}</View>
                )}
            </>
        )
    } else {
        content = renderDashed(buttonLabel, minHeight, onAdd)
    }

    return (
        <View testID={testID}>
            {content}

            <ActionSheet visible={ui.menuVisible} onClose={closeMenu} items={menuItems} />

            <Modal
                visible={ui.url.open}
                title="Lien externe"
                scrollable={false}
                dismissable
                onClose={() => dispatch({ type: "URL_CLOSE" })}
                footer={<Button label="OK" fullWidth onPress={submitUrl} />}
            >
                <TextInput
                    label="Lien"
                    value={ui.url.text}
                    onChangeText={(text) => dispatch({ type: "URL_TEXT", text })}
                    placeholder="https://…"
                    error={ui.url.error ?? undefined}
                    autoFocus
                    onSubmitEditing={submitUrl}
                    returnKeyType="done"
                    keyboardType="url"
                    autoCapitalize="none"
                    autoCorrect={false}
                />
            </Modal>

            <ConfirmDialog
                visible={ui.confirm != null}
                title={confirmTitle(ui.confirm)}
                onCancel={() => dispatch({ type: "CONFIRM", target: null })}
                onConfirm={() => {
                    if (ui.confirm) applyRemove(ui.confirm)
                    dispatch({ type: "CONFIRM", target: null })
                }}
            />

            {ui.viewer?.kind === "image" && (
                <MediaDisplay
                    kind="image"
                    visible
                    images={viewableImages}
                    onClose={() => dispatch({ type: "VIEWER", viewer: null })}
                />
            )}
            {ui.viewer?.kind === "pdf" && (
                <MediaDisplay
                    kind="pdf"
                    visible
                    uri={ui.viewer.uri}
                    onClose={() => dispatch({ type: "VIEWER", viewer: null })}
                />
            )}
            {ui.viewer?.kind === "link" && (
                <MediaDisplay
                    kind="link"
                    visible
                    url={ui.viewer.url}
                    onClose={() => dispatch({ type: "VIEWER", viewer: null })}
                />
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    addMore: {
        marginTop: theme.spacing["3"],
    },
}))
